import { useState } from 'react'
import Alert from '@cloudscape-design/components/alert'
import Box from '@cloudscape-design/components/box'
import Button from '@cloudscape-design/components/button'
import ColumnLayout from '@cloudscape-design/components/column-layout'
import Container from '@cloudscape-design/components/container'
import DatePicker from '@cloudscape-design/components/date-picker'
import FormField from '@cloudscape-design/components/form-field'
import Header from '@cloudscape-design/components/header'
import Input from '@cloudscape-design/components/input'
import Multiselect from '@cloudscape-design/components/multiselect'
import Select from '@cloudscape-design/components/select'
import SpaceBetween from '@cloudscape-design/components/space-between'
import StatusIndicator from '@cloudscape-design/components/status-indicator'
import Table from '@cloudscape-design/components/table'
import Tabs from '@cloudscape-design/components/tabs'
import Toggle from '@cloudscape-design/components/toggle'
import Link from '@cloudscape-design/components/link'
import { useAppStore } from '@/store/app-store'
import { credentialsManager } from '@/engine/credentials-manager'

const INSTANCE_TYPES = [
  'p6-b200.48xlarge', 'p6-b300.48xlarge',
  'p5.4xlarge', 'p5.48xlarge', 'p5e.48xlarge', 'p5en.48xlarge',
  'p4d.24xlarge', 'p4de.24xlarge',
  'trn1.32xlarge', 'trn2.48xlarge', 'trn2.3xlarge',
]

const AWS_REGIONS = [
  'us-east-1', 'us-east-2', 'us-west-1', 'us-west-2',
  'eu-north-1', 'eu-west-2',
  'ap-northeast-1', 'ap-northeast-2', 'ap-south-1',
  'ap-southeast-2', 'ap-southeast-3', 'ap-southeast-4',
  'sa-east-1',
]

const DURATION_OPTIONS = [
  ...[1,2,3,4,5,6,7,8,9,10,11,12,13,14].map(d => ({ value: String(d), label: `${d} day${d > 1 ? 's' : ''}` })),
  ...[21,28,35,42,49,56,63,70,77,84,91,98,105,112,119,126,133,140,147,154,161,168,175,182].map(d => ({ value: String(d), label: `${d} days (${Math.round(d/7)} weeks)` })),
]

interface CapacityResult {
  region: string
  instanceType: string
  instanceCount: string
  durationDays: string
  startDate: string
  endDate: string
  upfrontFee: string
  parts: string
  az: string
  error?: string
}

function formatDate(d: Date | string): string {
  const dt = typeof d === 'string' ? new Date(d) : d
  return dt.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })
}

async function getAwsCredentials() {
  const creds = await credentialsManager.getAwsCredentials()
  if (!creds) throw new Error('No AWS credentials configured. Go to Settings to add credentials.')
  return creds
}

async function scanEc2Region(region: string, instanceType: string, count: number, durationDays: number, startDate: string, endDate: string | null): Promise<CapacityResult[]> {
  try {
    const creds = await getAwsCredentials()
    const { EC2Client, DescribeCapacityBlockOfferingsCommand } = await import('@aws-sdk/client-ec2')
    const client = new EC2Client({
      region,
      credentials: {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        ...(creds.sessionToken ? { sessionToken: creds.sessionToken } : {}),
      },
    })
    const params: any = {
      InstanceType: instanceType,
      InstanceCount: count,
      CapacityDurationHours: durationDays * 24,
      StartDateRange: new Date(startDate),
      MaxResults: 100,
    }
    if (endDate) params.EndDateRange = new Date(endDate)
    const resp = await client.send(new DescribeCapacityBlockOfferingsCommand(params))
    return (resp.CapacityBlockOfferings ?? []).map((o: any) => ({
      region,
      instanceType,
      instanceCount: String(o.InstanceCount ?? 0),
      durationDays: ((o.CapacityBlockDurationHours ?? 0) / 24).toFixed(1),
      startDate: o.StartDate ? formatDate(o.StartDate) : 'N/A',
      endDate: o.EndDate ? formatDate(o.EndDate) : 'N/A',
      upfrontFee: `$${o.UpfrontFee ?? '0'}`,
      parts: String((o.ReservedCapacityOfferings ?? []).length || 1),
      az: o.AvailabilityZone ?? 'N/A',
    }))
  } catch (e: any) {
    // Suppress unhelpful errors: unsupported instance types, opt-in regions, etc.
    const msg = e.message ?? ''
    if (msg.includes('is not supported') || msg.includes('OptInRequired') || msg.includes('AuthFailure') || msg.includes('Unsupported') || msg.includes('not able to validate the provided access credentials')) return []
    return [{ region, instanceType, instanceCount: '', durationDays: '', startDate: '', endDate: '', upfrontFee: '', parts: '', az: '', error: msg }]
  }
}

async function scanSageMakerRegion(region: string, instanceType: string, count: number, durationDays: number, startDate: string, endDate: string | null): Promise<CapacityResult[]> {
  try {
    const creds = await getAwsCredentials()
    const { SageMakerClient, SearchTrainingPlanOfferingsCommand } = await import('@aws-sdk/client-sagemaker')
    const client = new SageMakerClient({
      region,
      credentials: {
        accessKeyId: creds.accessKeyId,
        secretAccessKey: creds.secretAccessKey,
        ...(creds.sessionToken ? { sessionToken: creds.sessionToken } : {}),
      },
    })
    const params: any = {
      TargetResources: ['training-job'],
      InstanceType: `ml.${instanceType}`,
      InstanceCount: count,
      StartTimeAfter: new Date(startDate),
      DurationHours: durationDays * 24,
    }
    if (endDate) params.EndTimeBefore = new Date(endDate)
    const resp = await client.send(new SearchTrainingPlanOfferingsCommand(params))
    return (resp.TrainingPlanOfferings ?? []).map((o: any) => {
      const r = (o.ReservedCapacityOfferings ?? [])[0]
      return {
        region,
        instanceType: r?.InstanceType ?? `ml.${instanceType}`,
        instanceCount: String(r?.InstanceCount ?? 0),
        durationDays: ((o.DurationHours ?? 0) / 24).toFixed(1),
        startDate: r?.StartTime ? formatDate(r.StartTime) : 'N/A',
        endDate: r?.EndTime ? formatDate(r.EndTime) : 'N/A',
        upfrontFee: `$${o.UpfrontFee ?? '0'}`,
        parts: String((o.ReservedCapacityOfferings ?? []).length),
        az: r?.AvailabilityZone ?? 'N/A',
      }
    })
  } catch (e: any) {
    const msg = e.message ?? ''
    if (msg.includes('InvalidAction') || msg.includes('AuthFailure') || msg.includes('is not supported') || msg.includes('OptInRequired') || msg.includes('Unsupported') || msg.includes('not able to validate the provided access credentials') || msg.includes('security token included in the request is invalid')) return []
    if (msg.includes('not allowlisted')) return [{ region, instanceType, instanceCount: '', durationDays: '', startDate: '', endDate: '', upfrontFee: '', parts: '', az: '', error: 'Account not allowlisted for Training Plans' }]
    return [{ region, instanceType, instanceCount: '', durationDays: '', startDate: '', endDate: '', upfrontFee: '', parts: '', az: '', error: msg }]
  }
}

function ErrorSummary({ errors }: { errors: CapacityResult[] }) {
  if (errors.length === 0) return null
  // Deduplicate by error message
  const byMsg = new Map<string, string[]>()
  for (const e of errors) {
    const msg = e.error ?? 'Unknown error'
    const list = byMsg.get(msg) ?? []
    list.push(e.region)
    byMsg.set(msg, list)
  }
  const uniqueRegions = new Set(errors.map(e => e.region))
  const entries = [...byMsg.entries()]
  // If a single error covers all queried regions, show it as an account-level issue
  const accountLevel = entries.filter(([, regions]) => regions.length >= uniqueRegions.size)
  const regionSpecific = entries.filter(([, regions]) => regions.length < uniqueRegions.size)

  return (
    <SpaceBetween size="xs">
      {accountLevel.map(([msg], i) => (
        <Alert key={`a-${i}`} type="warning">{msg}</Alert>
      ))}
      {regionSpecific.length > 0 && (
        <Alert type="warning" header={`${regionSpecific.reduce((n, [, r]) => n + r.length, 0)} region(s) returned errors`}>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {regionSpecific.map(([msg, regions], i) => (
              <li key={i}>{regions.join(', ')}: {msg}</li>
            ))}
          </ul>
        </Alert>
      )}
    </SpaceBetween>
  )
}

export function CapacityBlocksPage() {
  const executionMode = useAppStore((s) => s.executionMode)
  const credentialStatus = useAppStore((s) => s.credentialStatus)

  const today = new Date().toISOString().split('T')[0]
  const twoWeeks = new Date(Date.now() + 14 * 86400000).toISOString().split('T')[0]

  const [selectedInstances, setSelectedInstances] = useState([{ value: 'p5.48xlarge', label: 'p5.48xlarge' }])
  const [selectedRegions, setSelectedRegions] = useState([{ value: '__all__', label: 'All Regions' }])
  const [instanceCount, setInstanceCount] = useState('1')
  const [duration, setDuration] = useState({ value: '7', label: '7 days' })
  const [startDate, setStartDate] = useState(today)
  const [useEndDate, setUseEndDate] = useState(false)
  const [endDate, setEndDate] = useState(twoWeeks)
  const [loading, setLoading] = useState(false)
  const [ec2Results, setEc2Results] = useState<CapacityResult[]>([])
  const [smResults, setSmResults] = useState<CapacityResult[]>([])
  const [ec2Errors, setEc2Errors] = useState<CapacityResult[]>([])
  const [smErrors, setSmErrors] = useState<CapacityResult[]>([])
  const [activeTab, setActiveTab] = useState('ec2')
  const [scanDone, setScanDone] = useState(false)

  if (executionMode !== 'local') {
    return (
      <Container header={<Header variant="h2">Capacity Blocks Lookup</Header>}>
        <Alert type="info">
          Capacity Blocks lookup is only available in Local mode because results depend on your AWS account.
          Switch to Local mode from the top-right menu and configure your credentials in <Link onFollow={(e) => { e.preventDefault(); useAppStore.getState().setPage('settings') }}>Settings</Link>.
        </Alert>
      </Container>
    )
  }

  if (credentialStatus !== 'verified') {
    return (
      <Container header={<Header variant="h2">Capacity Blocks Lookup</Header>}>
        <Alert type="warning">
          AWS credentials required. Go to <Link onFollow={(e) => { e.preventDefault(); useAppStore.getState().setPage('settings') }}>Settings</Link> to configure and verify your credentials.
        </Alert>
      </Container>
    )
  }

  const regions = selectedRegions.some(r => r.value === '__all__') ? AWS_REGIONS : selectedRegions.map(r => r.value!)
  const instances = selectedInstances.map(i => i.value!)
  const count = Math.max(1, parseInt(instanceCount) || 1)
  const durationDays = parseInt(duration.value!) || 7

  const runScan = async (type: 'ec2' | 'sagemaker') => {
    setLoading(true)
    setScanDone(false)
    setActiveTab(type === 'ec2' ? 'ec2' : 'sagemaker')
    const scanFn = type === 'ec2' ? scanEc2Region : scanSageMakerRegion
    const allResults: CapacityResult[] = []

    const tasks = regions.flatMap(r => instances.map(i => scanFn(r, i, count, durationDays, startDate, useEndDate ? endDate : null)))
    const batched = await Promise.all(tasks)
    for (const batch of batched) allResults.push(...batch)

    const successes = allResults.filter(r => !r.error)
    const errors = allResults.filter(r => !!r.error)

    if (type === 'ec2') { setEc2Results(successes); setEc2Errors(errors) }
    else { setSmResults(successes); setSmErrors(errors) }

    setLoading(false)
    setScanDone(true)
  }

  const resultColumns = [
    { id: 'region', header: 'Region', cell: (r: CapacityResult) => r.region, sortingField: 'region' },
    { id: 'instanceType', header: 'Instance Type', cell: (r: CapacityResult) => r.instanceType },
    { id: 'instanceCount', header: 'Count', cell: (r: CapacityResult) => r.instanceCount },
    { id: 'durationDays', header: 'Duration (days)', cell: (r: CapacityResult) => r.durationDays },
    { id: 'startDate', header: 'Start Date', cell: (r: CapacityResult) => r.startDate },
    { id: 'endDate', header: 'End Date', cell: (r: CapacityResult) => r.endDate },
    { id: 'upfrontFee', header: 'Upfront Fee', cell: (r: CapacityResult) => r.upfrontFee },
    { id: 'parts', header: 'Parts', cell: (r: CapacityResult) => r.parts },
    { id: 'az', header: 'AZ', cell: (r: CapacityResult) => r.az },
  ]

  return (
    <SpaceBetween size="l">
      <Header variant="h1" description="Search for EC2 Capacity Block reservations and SageMaker Training Plan offerings across regions. Results depend on your AWS account.">
        Capacity Blocks Lookup
      </Header>

      <Container header={<Header variant="h2">Search Parameters</Header>}>
        <SpaceBetween size="m">
          <ColumnLayout columns={3}>
            <FormField label="Instance Types">
              <Multiselect
                selectedOptions={selectedInstances}
                onChange={({ detail }) => setSelectedInstances([...detail.selectedOptions] as any)}
                options={INSTANCE_TYPES.map(t => ({ value: t, label: t }))}
                placeholder="Select instance types"
              />
            </FormField>
            <FormField label="Regions">
              <Multiselect
                selectedOptions={selectedRegions}
                onChange={({ detail }) => setSelectedRegions([...detail.selectedOptions] as any)}
                options={[{ value: '__all__', label: 'All Regions' }, ...AWS_REGIONS.map(r => ({ value: r, label: r }))]}
                placeholder="Select regions"
              />
            </FormField>
            <FormField label="Instance Count">
              <Input type="number" value={instanceCount} onChange={({ detail }) => setInstanceCount(detail.value)} />
            </FormField>
          </ColumnLayout>
          <ColumnLayout columns={3}>
            <FormField label="Duration">
              <Select selectedOption={duration} onChange={({ detail }) => setDuration(detail.selectedOption as any)} options={DURATION_OPTIONS} />
            </FormField>
            <FormField label="Start Date">
              <DatePicker value={startDate} onChange={({ detail }) => setStartDate(detail.value)} placeholder="YYYY-MM-DD" />
            </FormField>
            <FormField label="End Date">
              <SpaceBetween size="xs">
                <Toggle checked={useEndDate} onChange={({ detail }) => setUseEndDate(detail.checked)}>Specify end date</Toggle>
                {useEndDate && <DatePicker value={endDate} onChange={({ detail }) => setEndDate(detail.value)} placeholder="YYYY-MM-DD" />}
              </SpaceBetween>
            </FormField>
          </ColumnLayout>
          <SpaceBetween direction="horizontal" size="s">
            <Button variant="primary" onClick={() => runScan('ec2')} loading={loading && activeTab === 'ec2'} disabled={loading || instances.length === 0}>
              Find EC2 Capacity Blocks
            </Button>
            <Button variant="normal" onClick={() => runScan('sagemaker')} loading={loading && activeTab === 'sagemaker'} disabled={loading || instances.length === 0}>
              Find SageMaker Training Plans
            </Button>
          </SpaceBetween>
        </SpaceBetween>
      </Container>

      {scanDone && (
        <Tabs
          activeTabId={activeTab}
          onChange={({ detail }) => setActiveTab(detail.activeTabId)}
          tabs={[
            {
              id: 'ec2',
              label: `EC2 Capacity Blocks (${ec2Results.length})`,
              content: (
                <SpaceBetween size="m">
                  {ec2Results.length === 0 && <StatusIndicator type="info">No EC2 capacity block offerings found.</StatusIndicator>}
                  {ec2Results.length > 0 && (
                    <Table columnDefinitions={resultColumns} items={ec2Results} stripedRows sortingDisabled variant="embedded"
                      header={<Header counter={`(${ec2Results.length})`}>EC2 Capacity Block Offerings</Header>} />
                  )}
                  {ec2Errors.length > 0 && <ErrorSummary errors={ec2Errors} />}
                </SpaceBetween>
              ),
            },
            {
              id: 'sagemaker',
              label: `SageMaker Plans (${smResults.length})`,
              content: (
                <SpaceBetween size="m">
                  {smResults.length === 0 && <StatusIndicator type="info">No SageMaker training plan offerings found.</StatusIndicator>}
                  {smResults.length > 0 && (
                    <Table columnDefinitions={resultColumns} items={smResults} stripedRows sortingDisabled variant="embedded"
                      header={<Header counter={`(${smResults.length})`}>SageMaker Training Plan Offerings</Header>} />
                  )}
                  {smErrors.length > 0 && <ErrorSummary errors={smErrors} />}
                </SpaceBetween>
              ),
            },
          ]}
        />
      )}
    </SpaceBetween>
  )
}
